import { FC } from 'react';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { navData } from '@/layouts/Default/config-nav.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { Link } from 'react-router-dom';
import { ArrowRightStartOnRectangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Routes from '@/router/Routes.ts';

type NavbarProps = {
  onClick?: () => void;
};

const Navbar: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick }) => {
  const { logout, userProfile } = useAuthContext();
  const { t } = useTranslation();
  const pathname = usePathname();
  const isAdmin = userProfile?.role === 'admin';

  return (
    <nav className={cn('flex flex-col justify-between grow', className)}>
      <div className="flex flex-col gap-3">
        {navData.map((item) => {
          const isActive = item.path === pathname;

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClick}
              className={cn(
                'flex items-center gap-2 px-4 h-9 rounded-lg text-white text-sm font-dm transition-colors',
                isActive ? 'bg-[#3D5585] font-bold' : 'font-normal hover:bg-white/10'
              )}
            >
              <div className="w-[18px] h-[18px] shrink-0">{item.icon}</div>
              <span>{t(item.title)}</span>
            </Link>
          );
        })}
        {isAdmin && (
          <Link
            to={Routes.ADMIN}
            onClick={onClick}
            className={cn(
              'flex items-center gap-2 px-4 h-9 rounded-lg text-white text-sm font-dm transition-colors',
              pathname.startsWith('/admin') ? 'bg-[#3D5585] font-bold' : 'font-normal hover:bg-white/10'
            )}
          >
            <div className="w-[18px] h-[18px] shrink-0">
              <ShieldCheckIcon />
            </div>
            <span>{t('navbar.admin')}</span>
          </Link>
        )}
      </div>

      <button
        onClick={logout}
        className="flex items-center gap-2 px-4 h-9 rounded-lg text-white text-sm font-medium font-dm hover:bg-white/10 transition-colors"
      >
        <ArrowRightStartOnRectangleIcon className="w-[18px] h-[18px] shrink-0" />
        <span>{t('button.logout')}</span>
      </button>
    </nav>
  );
};

export default Navbar;
